/*
  File: src/contexts/StoreStateContext.js
  Purpose: Manage store page state (filters, pagination) persistence
  Responsibilities:
  - Save store state when navigating to product details
  - Restore state when returning from product details
  - Clear state when navigating to other pages or logging out
*/
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const StoreStateContext = createContext({
  savedState: null,
  saveState: () => {},
  clearState: () => {},
  shouldRestore: false,
});

const STORE_STATE_KEY = 'store:state:v1';

export function StoreStateProvider({ children }) {
  const location = useLocation();
  const [savedState, setSavedState] = useState(null);
  const [lastPath, setLastPath] = useState(null);
  const [shouldRestore, setShouldRestore] = useState(false);

  // Load saved state from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORE_STATE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSavedState(parsed);
      }
    } catch (e) {
      console.error('Failed to load store state:', e);
    }
  }, []);

  // Track navigation patterns
  useEffect(() => {
    const currentPath = location.pathname;
    const isStorePage = currentPath === '/store';
    const isProductDetail = currentPath.startsWith('/store/') && currentPath !== '/store';
    
    // Only process navigation changes, not same-path updates
    if (currentPath === lastPath) {
      return;
    }
    
    // Coming from product detail back to store - should restore
    if (isStorePage && lastPath && lastPath.startsWith('/store/') && lastPath !== '/store') {
      setShouldRestore(true);
      setLastPath(currentPath);
      return;
    }
    
    // Coming from anywhere else to store (not from product detail) - should clear
    if (isStorePage && lastPath && !lastPath.startsWith('/store/')) {
      setShouldRestore(false);
      clearState();
      setLastPath(currentPath);
      return;
    }
    
    // First visit to store page (no lastPath) - don't restore
    if (isStorePage && !lastPath) {
      setShouldRestore(false);
      setLastPath(currentPath);
      return;
    }
    
    // Navigating away from store to non-store page - clear state
    if (lastPath === '/store' && !isStorePage && !isProductDetail) {
      clearState();
      setShouldRestore(false);
    }

    setLastPath(currentPath);
  }, [location.pathname]);

  const saveState = useCallback((state) => {
    try {
      const stateToSave = {
        ...state,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(STORE_STATE_KEY, JSON.stringify(stateToSave));
      setSavedState(stateToSave);
    } catch (e) {
      console.error('Failed to save store state:', e);
    }
  }, []);

  const clearState = useCallback(() => {
    try {
      sessionStorage.removeItem(STORE_STATE_KEY);
      setSavedState(null);
      setShouldRestore(false);
    } catch (e) {
      console.error('Failed to clear store state:', e);
    }
  }, []);

  const value = {
    savedState,
    saveState,
    clearState,
    shouldRestore,
  };

  return (
    <StoreStateContext.Provider value={value}>
      {children}
    </StoreStateContext.Provider>
  );
}

export function useStoreState() {
  return useContext(StoreStateContext);
}
